import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import { motion } from 'motion/react';
import {
  Card,
  Row,
  Col,
  Typography,
  Table,
  Tag,
  Avatar,
  Statistic,
  Tabs,
  Spin,
  Empty,
  List,
  Divider,
  Tooltip
} from 'antd';
import { UserOutlined, CheckCircleOutlined, ClockCircleOutlined, TrophyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import NeoBadge from 'src/RnRCommonComponent/CoreBadge/BadgeIcon/NeoBadge';
import { id } from 'date-fns/locale';

const { Title, Text } = Typography;

const QUERY_USERS_BY_COMPANY_MANAGER = gql`
  query QueryUsersByCompanyIdIndexManagerEmployeeId(
    $companyId: ID!
    $managerEmployeeId: String!
    $limit: Int
    $nextToken: String
  ) {
    queryUsersByCompanyIdIndexManagerEmployeeId(
      companyId: $companyId
      managerEmployeeId: $managerEmployeeId
      limit: $limit
      nextToken: $nextToken
    ) {
      nextToken
      items {
        companyId
        id
        firstName
        employeeId
        emailAddress
        accountClaimId
        active
        admin
        authMethod
        cognitoId
        careerProfile
        connectedApps
        createdById
        currency
        dateCreated
        dateFormat
        defaultDistance
        departmentId
        employeeType
        enableJobNotifications
        enableRecognition
        expirationDoneByToken
        expires
        recognitionEvents {
          additionalBudgetTotal
          amountPending
          currentBudget
          eventList {
            adminOverride
            budgetAfterTotal
            approvalResult
            budgetBeforeTotal
            completedById
            createdDate
            pendingApproval
            completedDate
            recognitionName
            recognitionId
            recognitionAmount
            sentById
            sentByManager
            sentReason
            snappyCampaignTierId
          }
          lastUpdated
        }
        lastName
        language
        lastLogin
        lastMobileLogin
        languageCode
      }
    }
  }
`;

// GraphQL query to get badge assignments for the team
const GET_TEAM_BADGE_ASSIGNMENTS = gql`query QueryBadgeAssignments(
    $companyId: ID!
    $badgeId: ID
    $userId: ID
    $departmentId: ID
    $approvalStatus: ApprovalStatus
    $managerEmployeeId: String
    $isPrivate: Boolean
    $after: AWSDateTime
    $before: AWSDateTime
    $limit: Int
    $nextToken: String
  ) {
    queryBadgeAssignments(
      companyId: $companyId
      badgeId: $badgeId
      userId: $userId
      departmentId: $departmentId
      approvalStatus: $approvalStatus
      managerEmployeeId: $managerEmployeeId
      isPrivate: $isPrivate
      after: $after
      before: $before
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        assignmentId
        userId
        badgeId
        badge {
          badgeTypeId
          badgeType {
            code
          }
        }
        companyId
        departmentId
        approvalStatus
        isPrivate
        hasPhysicalGift
        employeeId
        createdAt
        updatedAt
        snappyGrantId
      }
      nextToken
    }
  }
`;


const MyTeamComponent = (props) => {
  const currentUser = useSelector((state) => state.user.currentUser);
  const departmentId = currentUser?.departmentId;
  const companyId = currentUser?.company?.id;
  const baseSnappyBudget = currentUser?.company?.recognitionSettings?.baseSnappyBudget || 0;
  
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [badgeAssignments, setBadgeAssignments] = useState([]);
  const [nominations, setNominations] = useState([]);
  const [stats, setStats] = useState({
    totalMembers: 0,
    pendingApprovals: 0,
    approvedRecognitions: 0,
    totalNominations: 0
  });

  useEffect(() => {
    if (departmentId && companyId) {
      fetchTeamData();
    }
  }, [departmentId, companyId]);

  const fetchTeamData = async () => {
    setLoading(true);
    try {
      // Fetch team members using the filter for userGroupId
      const { data: teamData } = await props.client.query({
        query: QUERY_USERS_BY_COMPANY_MANAGER,
        variables: {
          companyId: companyId,
          managerEmployeeId: currentUser?.employeeId,
          limit: 1000,
        },
        fetchPolicy: 'network-only'
      });

      // Fetch badge assignments
      const { data: badgeData } = await props.client.query({
        query: GET_TEAM_BADGE_ASSIGNMENTS,
        variables: {
          companyId: companyId,
          managerEmployeeId: currentUser?.employeeId,
          limit: 1000
        },
        fetchPolicy: 'network-only'
      });

      if (teamData?.queryUsersByCompanyIdIndexManagerEmployeeId?.items) {
        setTeamMembers(teamData.queryUsersByCompanyIdIndexManagerEmployeeId.items);
      }
      const assignments = badgeData?.getBadgeAssignmentsByDepartmentId?.items || [];
      const nominationsFromQuery = [];
      assignments.forEach((assignment) => {
        if (assignment?.badge?.badgeType?.code === 'NOMINATED') {
          nominationsFromQuery.push(assignment);
        }
      });
      setBadgeAssignments(assignments);
      setNominations(nominationsFromQuery);
      // Calculate stats
      const pendingCount = assignments.filter(a => a.approvalStatus === 'Pending').length;
      const approvedCount = assignments.filter(a => a.approvalStatus === 'Approved').length;
        
      setStats({
        totalMembers: teamData?.queryUsersByCompanyIdIndexManagerEmployeeId?.items?.length || 0,
        pendingApprovals: pendingCount,
        approvedRecognitions: approvedCount,
        totalNominations: nominationsFromQuery?.length
      });
    } catch (error) {
      console.error('Error fetching team data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Team members columns
  const teamMembersColumns = [
    {
      title: 'Employee',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            icon={<UserOutlined />}
            src={record.additionalInfo?.avatar?.key ? `path-to-avatar/${record.additionalInfo.avatar.key}` : null}
            style={{ marginRight: 8 }}
          />
          <span>{`${record.firstName} ${record.lastName}`}</span>
        </div>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Email',
      dataIndex: 'emailAddress',
      key: 'email',
    },
    {
      title: 'Employee ID',
      dataIndex: 'employeeId',
      key: 'employeeId',
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'status',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Budget',
      dataIndex: 'additionalInfo',
      key: 'budget',
      render: (additionalInfo) => {
        const currentBudget = additionalInfo?.recognitionEvents?.currentBudget || 0;
        const totalBudget = currentBudget + baseSnappyBudget;
        return (
          <span style={{ color: totalBudget > 0 ? 'green' : 'red' }}>
            ${totalBudget}
          </span>
        );
      },
    }
  ];

  // Recognition columns
  const recognitionColumns = [
    {
      title: 'Recipient',
      dataIndex: 'user',
      key: 'recipient',
      render: (user) => `${user?.firstName || ''} ${user?.lastName || ''}`,
    },
    {
      title: 'Badge',
      dataIndex: 'badge',
      key: 'badge',
      render: (badge, record) => {
        // Check if we should show the Snappy icon
        const hasSnappyId = !!badge?.snappyCampaignTierId;
        const isManagerOrAdmin = ['manager', 'admin', 'superAdmin', 'supportAdmin'].includes(currentUser?.role?.toLowerCase());
        const hideSnappyIcon = !(hasSnappyId && isManagerOrAdmin);
        const currencySymbol = badge?.snappyGrant?.currencySymbol || '$';
        
        // Prepare snappy details if available
        const snappyDetails = hasSnappyId ? {
          id: badge.snappyGrantId,
          name: badge.snappyGrant?.name || 'Snappy Reward',
          costInCurrency: `${currencySymbol}${badge.snappyGrant?.costInCurrency || 0}`
        } : {};
        
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ marginRight: '10px', width: '40px', height: '40px' }}>
              <NeoBadge
                icon={badge?.icon || "AWARD"}
                text={badge?.name || "Badge"}
                shape={badge?.shape || "hexagon"}
                colors={{
                  gradient: badge?.gradient || "linear-gradient(to bottom right, #6366f1, #0ea5e9, #10b981)",
                  icon: "#ffffff",
                  text: "#ffffff"
                }}
                hideSnappyIcon={hideSnappyIcon}
                snappyDetails={snappyDetails}
                useTilt={false}
              />
            </div>
            <span>{badge?.name || 'Unknown Badge'}</span>
          </div>
        );
      },
    },
    {
      title: 'Snappy Reward',
      dataIndex: 'badge',
      key: 'tier',
      render: (badge) => badge?.snappyGrant?.name || 'N/A',
    },
    {
      title: 'Total Cost',
      dataIndex: 'badge',
      key: 'costInCurrency',
      render: (badge) => `${badge?.snappyGrant?.currencySymbol || '$'}${badge?.snappyGrant?.costInCurrency || 0}`,
    },
    {
      title: 'Created By',
      dataIndex: 'awardedBy',
      key: 'awardedBy',
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'created',
      render: (date) => dayjs(date).format('MM/DD/YYYY'),
    },
    {
      title: 'Status',
      dataIndex: 'approvalStatus',
      key: 'status',
      render: (status) => (
        <Tag color={
          status === 'Approved' ? 'green' : 
          status === 'Pending' ? 'gold' : 
          status === 'Declined' ? 'red' : 'default'
        }>
          {status}
        </Tag>
      ),
    },
  ];

  if (!departmentId) {
    return (
      <Card>
        <Empty description="You don't have a team assigned to you." />
      </Card>
    );
  }

  return (
    <div className="my-team-container">
      <Title level={2}>My Team Dashboard</Title>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* Stats Cards*/}
          <motion.div
            className="contact-color-card"
            style={{
              marginBottom: 24,
              width: '100%',
              gap: '20px',
              justifyContent: 'space-between'
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <motion.div
              className="color-card"
              style={{
                background: 'rgb(20, 214, 211)',
                flex: 1,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)'
              }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
              whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
            >
              <h4 style={{ fontWeight: 700 }}>{stats.totalMembers}</h4>
              <p style={{ fontWeight: 500 }}>Team Members</p>
            </motion.div>
            <motion.div
              className="color-card"
              style={{
                background: 'rgb(231, 93, 167)',
                flex: 1,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)'
              }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
              whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
            >
              <h4 style={{ fontWeight: 700 }}>{stats.pendingApprovals}</h4>
              <p style={{ fontWeight: 500 }}>Pending Approvals</p>
            </motion.div>
            <motion.div
              className="color-card"
              style={{
                background: 'rgb(130, 106, 230)',
                flex: 1,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)'
              }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3, ease: "easeOut" }}
              whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
            >
              <h4 style={{ fontWeight: 700 }}>{stats.approvedRecognitions}</h4>
              <p style={{ fontWeight: 500 }}>Approved Recognitions</p>
            </motion.div>
            <motion.div
              className="color-card"
              style={{
                background: 'rgb(36, 46, 63)',
                flex: 1,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)'
              }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
              whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
            >
              <h4 style={{ fontWeight: 700 }}>{stats.totalNominations}</h4>
              <p style={{ fontWeight: 500 }}>Total Nominations</p>
            </motion.div>
          </motion.div>

          <Tabs
            defaultActiveKey="team"
            items={[
              {
                key: 'team',
                label: 'Team Members',
                children: (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  >
                    <Table
                      dataSource={teamMembers}
                      columns={teamMembersColumns}
                      rowKey="id"
                      pagination={{ pageSize: 10 }}
                    />
                  </motion.div>
                )
              },
              {
                key: 'pending',
                label: 'Pending Approvals',
                children: (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  >
                    <Table
                      dataSource={badgeAssignments.filter(item => item.approvalStatus === 'Pending')}
                      columns={recognitionColumns}
                      rowKey="id"
                      pagination={{ pageSize: 10 }}
                      locale={{ emptyText: 'No pending approvals' }}
                    />
                  </motion.div>
                )
              },
              {
                key: 'approved',
                label: 'Recent Approvals',
                children: (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  >
                    <Table
                      dataSource={badgeAssignments.filter(item => item.approvalStatus === 'Approved')}
                      columns={recognitionColumns}
                      rowKey="id"
                      pagination={{ pageSize: 10 }}
                      locale={{ emptyText: 'No approved recognitions' }}
                    />
                  </motion.div>
                )
              },
              {
                key: 'all',
                label: 'All Recognitions',
                children: (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  >
                    <Table
                      dataSource={badgeAssignments}
                      columns={recognitionColumns}
                      rowKey="id"
                      pagination={{ pageSize: 10 }}
                      locale={{ emptyText: 'No recognitions found' }}
                    />
                  </motion.div>
                )
              }
            ]}
          />
        </>
      )}
    </div>
  );
};

export default withApollo(MyTeamComponent);